/**
 * Language toggle.
 *
 * A two-option group rather than a dropdown: there are exactly two languages, and an employee
 * switching mid-task should not need two clicks. Each option is a toggle button with
 * `aria-pressed`, so a screen reader announces which language is active.
 *
 * The option labels are the language's own name ("English", "العربية") and both appear in both
 * locale files, so a user who lands in the wrong language can still recognise the way out.
 */

import { useTranslation } from "react-i18next";

import { type Language } from "../i18n/config";
import { useLanguage } from "../i18n/useLanguage";

export interface LanguageToggleProps {
  /**
   * The two-letter form — "EN | AR" — as the conversation rail's footer card uses.
   *
   * The full names stay the default and stay right nearly everywhere: someone who has landed in a
   * language they cannot read needs to recognise the way out, and "العربية" is recognisable where
   * "AR" is a guess. The rail is the exception the design makes, and it can afford to be: the
   * toggle sits in a card the employee has already learned, beside a sign-out they use daily.
   */
  compact?: boolean;
}

export function LanguageToggle({ compact = false }: LanguageToggleProps = {}): JSX.Element {
  const { t } = useTranslation();
  const { language, languages, setLanguage } = useLanguage();

  if (compact) {
    return (
      <div
        className="flex items-center gap-2 text-label-sm font-bold"
        role="group"
        aria-label={t("language.switcher")}
      >
        {languages.map((option: Language, index: number) => (
          <span key={option} className="flex items-center gap-2">
            {index > 0 ? (
              <span aria-hidden="true" className="font-normal text-borderStrong">
                |
              </span>
            ) : null}
            <button
              type="button"
              lang={option}
              aria-pressed={option === language}
              title={t("language.switchTo", { language: t(`languages.${option}`) })}
              onClick={() => setLanguage(option)}
              className={
                option === language
                  ? "text-primary transition-colors"
                  : "text-textMuted transition-colors hover:text-primary"
              }
            >
              {/* The accessible name stays the language's own name; only the visible label is
                  abbreviated. A screen reader announcing "AR" would be worse than useless. */}
              <span aria-hidden="true">{option.toUpperCase()}</span>
              <span className="sr-only">{t(`languages.${option}`)}</span>
            </button>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className="nab-language-toggle"
      role="group"
      aria-label={t("language.switcher")}
    >
      {languages.map((option: Language) => (
        <button
          key={option}
          type="button"
          className="nab-language-toggle__option"
          aria-pressed={option === language}
          // `lang` on the button so the browser shapes each name with the right font and the
          // screen reader pronounces it in its own language.
          lang={option}
          title={t("language.switchTo", { language: t(`languages.${option}`) })}
          onClick={() => setLanguage(option)}
        >
          {t(`languages.${option}`)}
        </button>
      ))}
    </div>
  );
}
